/**
 * Platform Health — Email Delivery (A7Y-2333).
 *
 * Failure, bounce and complaint rates per send path from the A7Y-2321 delivery
 * monitor, plus the recent failures with their reasons.
 *
 * The silent-path banner is the reason this page is more than a table: a send
 * path that is sending NOTHING looks identical to one with a perfect delivery
 * rate — both show zero failures. A path that sent last week and nothing this
 * week is called out rather than quietly vanishing from the list.
 */
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DeliveryPathSummary } from "@/pages/platform-health/types";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, MailX, RefreshCw } from "lucide-react";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function relative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "unknown";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function rateColor(rate: number): string {
  if (rate >= 10) return "text-red-600 dark:text-red-400";
  if (rate >= 2) return "text-amber-600 dark:text-amber-400";
  return "text-green-600 dark:text-green-400";
}

function PathRow({ row }: { row: DeliveryPathSummary }) {
  return (
    <li
      data-testid="platform-health-delivery-path"
      className="flex items-center gap-3 border-b border-gray-200 dark:border-zinc-700 px-4 py-3 last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{row.sendPath}</p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {row.total} messages · {row.delivered} delivered · {row.failed} failed · {row.bounced}{" "}
          bounced · {row.complained} complaints
          {" · "}{row.suppressed ?? 0} provider suppressed · {row.policySuppressed ?? 0} policy suppressed
          {" · "}{row.retrying ?? 0} retrying · {row.delayed ?? 0} delayed · {row.staleInFlight ?? 0} stale in flight
          {" · "}{row.cancelled ?? 0} cancelled · {row.unknown ?? 0} unknown
          {" · "}{row.coverageDebt ?? 0} with incomplete identity or path attribution
        </p>
      </div>
      <div className="shrink-0 text-right">
        <div className={cn("text-sm font-semibold tabular-nums", rateColor(row.failureRate))}>
          {row.failureRate}%
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">failure rate</div>
      </div>
    </li>
  );
}

export function EmailDeliveryPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["platform-health", "email-delivery"],
    queryFn: () => api.getPlatformHealthEmailDelivery(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const payload = data?.data;
  const byPath = payload?.byPath ?? [];

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Email Delivery"
        subtitle="Email send failures, bounces, and delivery health"
      />

      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        One current state per message, including failures before provider acceptance. Rates use
        attempted messages in the creation cohort; scheduled, cancelled, unknown and policy-suppressed
        messages are excluded. Failure includes exhausted or unknown-retry failures, bounces,
        complaints and provider suppressions. Retryable failures are shown separately. Policy
        suppression uses all messages as its denominator. Stale means in flight for at least 24 hours.
        Late provider events can update these rates; history and retry attempts remain preserved.
      </p>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {payload?.generatedAt ? `Updated ${relative(payload.generatedAt)}` : "Loading…"}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-zinc-600 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-700"
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>

      {isError ? (
        <div
          data-testid="platform-health-delivery-error"
          className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-4 text-sm text-red-800 dark:text-red-200"
        >
          Couldn&apos;t load delivery health: {(error as Error)?.message || "unknown error"}
        </div>
      ) : null}

      {/* A silent path shows zero failures, exactly like a perfect one. */}
      {payload && payload.silentPaths.length > 0 ? (
        <div
          data-testid="platform-health-delivery-silent"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <MailX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Sent last week but nothing this week: <strong>{payload.silentPaths.join(", ")}</strong>.
            A silent path reports zero failures exactly like a healthy one, so check whether it
            stopped sending.
          </span>
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading delivery health…
        </div>
      ) : null}

      {byPath.length > 0 ? (
        <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              By send path (7 days, prod, excluding synthetic)
            </h3>
          </header>
          <ul>
            {byPath.map((row) => (
              <PathRow key={row.sendPath} row={row} />
            ))}
          </ul>
        </section>
      ) : payload ? (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-12 text-center text-sm text-amber-800 dark:text-amber-200">
          No non-synthetic prod email observed in the last 7 days. That is a finding, not an
          all-clear.
        </div>
      ) : null}

      {payload && payload.recentFailures.length > 0 ? (
        <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Recent non-delivery events (14 days)
            </h3>
          </header>
          <ul>
            {payload.recentFailures.map((f, idx) => (
              <li
                key={`${f.observed_at}-${idx}`}
                className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5 last:border-b-0"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {f.outcome_class}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">{f.send_path}</span>
                  {f.recipient_domain ? (
                    <span className="text-gray-500 dark:text-gray-400">@{f.recipient_domain}</span>
                  ) : null}
                  <span className="text-gray-400 dark:text-zinc-500">
                    {relative(f.observed_at)}
                  </span>
                </div>
                {f.subject ? (
                  <p className="mt-0.5 truncate text-sm text-gray-700 dark:text-gray-200">
                    {f.subject}
                  </p>
                ) : null}
                {f.error_message ? (
                  <p className="mt-0.5 flex items-start gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                    {f.error_message}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
